import { ExportOutlined } from '@ant-design/icons';
import { App, Avatar, TableProps, Tag } from 'antd';
import clipboard from 'clipboardy';
import { useMemo } from 'react';
import { FaRegCopy, FaRegEdit } from 'react-icons/fa';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { API_BASEURL } from '../../../common/constants';
import { TActionList } from '../../../common/types';
import {
  ActionListButton,
  AppDrawer,
  DataTable,
  drawerResource,
  openDrawer,
  useAppDispatch,
  useAuthUser,
} from '../../../core';
import { useGetCategoriesQuery } from '../store/category.api';
import { ICategoryAttributes } from '../types';
import { UpdateCategory } from './update';

export type TCategoryDrawer = 'CategoryDrawer';

export const CategoryList = () => {
  const { data: categories, isFetching, error } = useGetCategoriesQuery();
  const dispatch = useAppDispatch();
  const { auth: { access_token: accessToken } = {} } = useAuthUser();
  const { message } = App.useApp();

  const actions: TActionList['items'] = [
    {
      label: 'Edit',
      key: 'edit',
      icon: <FaRegEdit />,
      action: (record) => {
        dispatch(
          drawerResource({
            id: record.key,
            type: 'Category',
            attributes: record,
          }),
        );
        dispatch(openDrawer('CategoryDrawer'));
      },
    },
    {
      label: 'Delete',
      key: 'delete',
      icon: <RiDeleteBin5Fill />,
      disabled: true,
      action: (record) => {},
    },
    {
      label: 'Export Report',
      key: 'export_report',
      icon: <ExportOutlined />,
      disabled: true,
      action: (record) => {
        window.open(
          encodeURI(
            `${API_BASEURL}/categories/export?token=${accessToken}&filter[category_id]=${record.key}`,
          ),
          '_blank',
        );
      },
    },
    {
      label: 'Copy Link',
      key: 'copy_link',
      icon: <FaRegCopy />,
      disabled: true,
      action: (record) => {
        clipboard.write(
          `https://superapp.rahetbally.com/categories/${record.key}`,
        );
        message.success('Link copied to clipboard');
      },
    },
  ];

  const columns: TableProps<ICategoryAttributes>['columns'] = useMemo(
    () => [
      {
        title: 'Icon',
        dataIndex: 'icon',
        width: 100,
        align: 'center',
        render: (icon) => <Avatar src={icon} />,
      },
      {
        title: 'Order',
        dataIndex: 'order',
        width: 100,
        align: 'center',
        sorter: {
          compare: (a, b) => a.order - b.order,
          multiple: 1,
        },
      },
      {
        title: 'Name (English)',
        dataIndex: 'name_en',
        sorter: {
          compare: (a, b) => a.name_en.localeCompare(b.name_en),
          multiple: 2,
        },
      },
      {
        title: 'Name (Arabic)',
        dataIndex: 'name_ar',
        sorter: {
          compare: (a, b) => a.name_ar.localeCompare(b.name_ar),
          multiple: 3,
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        align: 'center',
        width: 100,
        render: (status) => (
          <Tag color={status ? 'green' : 'red'}>
            {status ? 'Active' : 'Inactive'}
          </Tag>
        ),
        sorter: {
          compare: (a, b) => (a.status ? 1 : 0) - (b.status ? 1 : 0),
          multiple: 4,
        },
        filters: [
          {
            text: 'Active',
            value: true,
          },
          {
            text: 'Inactive',
            value: false,
          },
        ],
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 110,
        render: (_, record) => (
          <ActionListButton items={actions} record={record} />
        ),
      },
    ],
    [actions],
  );

  // Handle error state
  if (error) {
    message.error('Failed to load categories');
  }

  return (
    <>
      <AppDrawer title="Update Category" drawerId="CategoryDrawer">
        <UpdateCategory />
      </AppDrawer>
      <DataTable
        paginated={false}
        columns={columns}
        data={categories}
        isLoading={isFetching}
      />
    </>
  );
};
